/**
 * Central storage migration utility for managing localStorage across deploys.
 *
 * When the app deploys a new version, this module clears all deprecated
 * localStorage keys to prevent stale state from interfering with new code.
 *
 * To deprecate a key: move it from CURRENT_STORAGE_KEYS to DEPRECATED_STORAGE_KEYS.
 * To add a new key: add it to CURRENT_STORAGE_KEYS.
 *
 * NOTE: auth-storage uses sessionStorage (not localStorage) and is NOT managed here.
 */

/** All localStorage keys actively used by the current version of the app. */
const CURRENT_STORAGE_KEYS = [
  // Zustand persisted stores
  'global-storage',
  'process-analysis-storage',
  'financials-store',
  'work-period-store',
  'strategy-storage',
  'process-storage',
  'adherence-store',
  'dashboards-store',
  'agentic-process-storage',
  'agent-miner-storage',
  'data-management-storage',
  'integrations-store',
  // Non-store localStorage keys
  'process-scope',
  'flora-active-job',
  'bloomfilter_post_login_redirect',
  'selectedPortfolioBeforeRedirectToExternalSystem',
  'jiraInstance',
] as const;

/** localStorage keys from previous versions that should be removed on deploy. */
// 'flora-store' — the flora Zustand store was removed in the dead-code cleanup; its
// persisted localStorage blob is deprecated so it is cleared on the next deploy.
const DEPRECATED_STORAGE_KEYS = ['process-analysis-v1-storage', 'process-analysis-v2-storage', 'flora-store'] as const;

/**
 * Removes all deprecated localStorage keys.
 * Called during version mismatch to clean up stale state from previous deploys.
 */
function clearDeprecatedStorage(): void {
  for (const key of DEPRECATED_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
}

/**
 * Removes ALL app-managed localStorage keys (both current and deprecated).
 * Use this for a full reset — e.g., on logout or when a hard migration is needed.
 */
function clearAllAppStorage(): void {
  for (const key of CURRENT_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  clearDeprecatedStorage();
}

export { clearAllAppStorage };
